import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { clearTokens } from "@/lib/token-manager"
import { useAuthForm } from "../hooks/useAuthForm"
import { type ResetPasswordFormData, resetPasswordSchema } from "../schemas/auth.schema"
import { resetPassword } from "../services/auth.service"

export function ResetPasswordPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get("token") || ""
  const email = searchParams.get("email") || ""

  const [formState, setFormState] = useState<"default" | "success" | "error">("default")
  const [errorMessage, setErrorMessage] = useState("")

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    resetPasswordSchema,
    async (data: ResetPasswordFormData) => {
      await resetPassword(email, token, data.password)
      setFormState("success")
    },
  )

  // Validate token on mount
  useEffect(() => {
    if (!token || !email) {
      setFormState("error")
      setErrorMessage(t("resetPassword.invalidToken"))
    }
  }, [token, email, t])

  const passwordRegister = form.register("password")
  const confirmPasswordRegister = form.register("confirmPassword")

  if (formState === "error") {
    return (
      <AuthWrapper>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-5">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.errorTitle")}
            </h2>
          </div>

          <div className="space-y-4 text-center">
            <p className="text-primary text-sm">
              {errorMessage || t("resetPassword.errorDescription")}
            </p>
          </div>

          <div className="py-2">
            <Button
              type="button"
              variant="primary"
              size="md"
              className="uppercase"
              fullWidth
              onClick={() => navigate("/forgot-password")}
            >
              {t("resetPassword.requestNewLink")}
            </Button>
          </div>
        </div>
      </AuthWrapper>
    )
  }

  if (formState === "success") {
    return (
      <AuthWrapper>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-5">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.successTitle")}
            </h2>
          </div>

          <div className="space-y-2 text-center">
            <p className="text-primary text-[13px]">{t("resetPassword.successDescription")}</p>
            <p className="text-primary text-[13px]">{t("resetPassword.successDescription2")}</p>
          </div>

          <div className="py-2 flex justify-center">
            <Button
              type="button"
              variant="primary"
              size="md"
              className="uppercase max-w-[200px]"
              fullWidth
              onClick={() => {
                // Clear tokens and navigate to login
                clearTokens()
                navigate("/login", { replace: true })
              }}
            >
              {t("resetPassword.loginButton")}
            </Button>
          </div>
        </div>
      </AuthWrapper>
    )
  }

  return (
    <AuthWrapper>
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.title")}
            </h2>
            <p className="text-primary text-sm mt-2">{t("resetPassword.description")}</p>
          </div>

          <div className="space-y-6 px-2">
            <CustomInput
              label={t("resetPassword.passwordLabel")}
              type="password"
              required={false}
              placeholder={t("resetPassword.passwordPlaceholder")}
              {...passwordRegister}
              error={(form.formState.errors.password?.message || serverError) as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                passwordRegister.onChange?.(e)
                clearServerError()
              }}
            />

            <CustomInput
              label={t("resetPassword.confirmPasswordLabel")}
              type="password"
              required={false}
              placeholder={t("resetPassword.confirmPasswordPlaceholder")}
              {...confirmPasswordRegister}
              error={form.formState.errors.confirmPassword?.message as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                confirmPasswordRegister.onChange?.(e)
                clearServerError()
              }}
            />

            <div className="py-2 flex justify-center">
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="uppercase max-w-[200px]"
                isLoading={isSubmitting}
                fullWidth
              >
                {t("resetPassword.submitButton")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </AuthWrapper>
  )
}
